import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  message,
  Tag,
  Statistic,
  Select,
  Descriptions,
  Popconfirm
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  BarChartOutlined,
  GlobalOutlined,
  SecurityScanOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const AdminAttackIPMonitor = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('attackCount');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [serviceControlLoading, setServiceControlLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [geoDistribution, setGeoDistribution] = useState(null);


  // 获取攻击IP监控数据
  const fetchData = useCallback(async (page = 1, limit = 20, search = '', sortField = sortBy, sortDirection = sortOrder) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
        sortBy: sortField,
        sortOrder: sortDirection
      });

      const response = await API.get(`/api/admin/attack-ip-monitor/data?${params}`);
      
      if (response.data && response.data.data) {
        setData(response.data.data);
        setPagination({
          current: response.data.data.pagination.currentPage,
          pageSize: response.data.data.pagination.pageSize,
          total: response.data.data.pagination.totalCount
        });
      }
    } catch (error) {
      console.error('获取攻击IP监控数据失败:', error);
      message.error(error.response?.data?.message || '获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  // 获取地理分布统计数据
  const fetchGeoDistribution = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/attack-ip-monitor/geo-distribution', {
        params: {
          sortBy: 'totalAttacks',
          sortOrder: 'desc',
          minAttacks: 1
        }
      });
      
      setGeoDistribution(response.data.data);
    } catch (error) {
      console.error('获取地理分布统计失败:', error);
      message.error('获取地理分布统计失败，请稍后重试');
    }
  }, []);

  // 初始加载数据
  useEffect(() => {
    fetchData();
    fetchGeoDistribution();
  }, [fetchData, fetchGeoDistribution]);

  // 搜索处理
  const handleSearch = (value) => {
    setSearchText(value);
    fetchData(1, pagination.pageSize, value, sortBy, sortOrder);
  };

  // 排序处理
  const handleSortChange = (field, direction) => {
    setSortBy(field);
    setSortOrder(direction);
    fetchData(pagination.current, pagination.pageSize, searchText, field, direction);
  };

  // 分页处理
  const handleTableChange = (paginationConfig, filters, sorter) => {
    let sortField = sortBy;
    let sortDirection = sortOrder;
    
    if (sorter.field) {
      sortField = sorter.field;
      sortDirection = sorter.order === 'descend' ? 'desc' : 'asc';
      setSortBy(sortField);
      setSortOrder(sortDirection);
    }

    fetchData(paginationConfig.current, paginationConfig.pageSize, searchText, sortField, sortDirection);
  };

  // 控制服务
  const handleServiceControl = async (action) => {
    setServiceControlLoading(true);
    try {
      const response = await API.post('/api/admin/attack-ip-monitor/control', { action });
      message.success(response.data.message);
      // 重新获取数据以更新服务状态
      fetchData(pagination.current, pagination.pageSize, searchText, sortBy, sortOrder);
    } catch (error) {
      console.error('控制服务失败:', error);
      message.error(error.response?.data?.message || '控制服务失败');
    } finally {
      setServiceControlLoading(false);
    }
  };

  // 手动触发任务
  const handleTrigger = async () => {
    setTriggerLoading(true);
    try {
      const response = await API.post('/api/admin/attack-ip-monitor/trigger');
      message.success(response.data.message);
      // 延迟重新获取数据
      setTimeout(() => {
        fetchData(pagination.current, pagination.pageSize, searchText, sortBy, sortOrder);
      }, 2000);
    } catch (error) {
      console.error('触发任务失败:', error);
      message.error(error.response?.data?.message || '触发任务失败');
    } finally {
      setTriggerLoading(false);
    }
  };

  // 清空数据
  const handleClearData = async () => {
    setClearLoading(true);
    try {
      const response = await API.delete('/api/admin/attack-ip-monitor/data');
      message.success(`${response.data.message}，删除了 ${response.data.deletedCount} 条记录`);
      // 重新获取数据
      fetchData(1, pagination.pageSize, '', sortBy, sortOrder);
      setSearchText('');
    } catch (error) {
      console.error('清空数据失败:', error);
      message.error(error.response?.data?.message || '清空数据失败');
    } finally {
      setClearLoading(false);
    }
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchData(pagination.current, pagination.pageSize, searchText, sortBy, sortOrder);
    fetchGeoDistribution();
  };

  // 表格列定义
  const columns = [
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: '15%',
      render: (ip) => <Text code>{ip}</Text>,
      sorter: true,
      sortOrder: sortBy === 'ip' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null
    },
    {
      title: 'IP归属地',
      dataIndex: 'ipLocation',
      key: 'ipLocation',
      width: '20%',
      render: (location) => (
        <Tag color="blue" icon={<GlobalOutlined />}>
          {location || '未知'}
        </Tag>
      )
    },
    {
      title: '攻击次数',
      dataIndex: 'attackCount',
      key: 'attackCount',
      width: '12%',
      render: (count) => (
        <Text strong style={{ color: count > 100 ? '#ff4d4f' : count > 50 ? '#fa8c16' : '#52c41a' }}>
          {count.toLocaleString()}
        </Text>
      ),
      sorter: true,
      sortOrder: sortBy === 'attackCount' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '18%',
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-',
      sorter: true,
      sortOrder: sortBy === 'createdAt' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null
    },
    {
      title: '最后发现时间',
      dataIndex: 'lastSeenTime',
      key: 'lastSeenTime',
      width: '18%',
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-',
      sorter: true,
      sortOrder: sortBy === 'lastSeenTime' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '17%',
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-',
      sorter: true,
      sortOrder: sortBy === 'updatedAt' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null
    }
  ];

  // 服务状态颜色
  const getServiceStatusColor = (serviceStatus) => {
    if (serviceStatus?.isRunning) {
      return 'success';
    }
    return 'default';
  };

  // 服务状态文本
  const getServiceStatusText = (serviceStatus) => {
    if (serviceStatus?.isRunning) {
      return '运行中';
    }
    return '已停止';
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          <SecurityScanOutlined style={{ marginRight: '8px' }} />
          攻击IP监控管理
        </Title>

        {/* 服务状态和控制 */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]} align="middle">
            <Col span={24}>
              <Space size="large" wrap>
                <div>
                  <Text strong>服务状态：</Text>
                  <Tag 
                    color={getServiceStatusColor(data?.statistics?.serviceStatus)} 
                    style={{ marginLeft: '8px' }}
                  >
                    {getServiceStatusText(data?.statistics?.serviceStatus)}
                  </Tag>
                </div>
                
                {data?.statistics?.serviceStatus?.lastRunTime && (
                  <div>
                    <Text strong>最后运行时间：</Text>
                    <Text>{new Date(data.statistics.serviceStatus.lastRunTime).toLocaleString('zh-CN')}</Text>
                  </div>
                )}

                <div>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={() => handleServiceControl('start')}
                      loading={serviceControlLoading}
                      disabled={data?.statistics?.serviceStatus?.isRunning}
                    >
                      启动服务
                    </Button>
                    <Button
                      icon={<PauseCircleOutlined />}
                      onClick={() => handleServiceControl('stop')}
                      loading={serviceControlLoading}
                      disabled={!data?.statistics?.serviceStatus?.isRunning}
                    >
                      停止服务
                    </Button>
                    <Button
                      type="dashed"
                      icon={<ThunderboltOutlined />}
                      onClick={handleTrigger}
                      loading={triggerLoading}
                    >
                      手动触发
                    </Button>
                    <Popconfirm
                      title="确认清空数据"
                      description="此操作将删除所有攻击IP监控数据，且无法恢复。确定继续吗？"
                      onConfirm={handleClearData}
                      okText="确定"
                      cancelText="取消"
                      icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                    >
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        loading={clearLoading}
                      >
                        清空数据
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 统计信息 */}
        {data?.statistics?.summary && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="监控IP总数"
                  value={data.statistics.summary.totalIPs}
                  prefix={<GlobalOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="攻击总次数"
                  value={data.statistics.summary.totalAttacks}
                  prefix={<SecurityScanOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="平均攻击次数"
                  value={data.statistics.summary.avgAttacksPerIP}
                  precision={1}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="最大攻击次数"
                  value={data.statistics.summary.maxAttacksPerIP}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 详细地理分布统计 */}
        {geoDistribution && (
          <Card title="地理分布统计" style={{ marginBottom: '24px' }}>
            {/* 总体统计 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
              <Col xs={24} sm={12} md={6}>
                <Card size="small" style={{ textAlign: 'center', background: '#f6ffed' }}>
                  <Statistic
                    title="覆盖地区"
                    value={geoDistribution.summary.totalLocations}
                    suffix="个"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small" style={{ textAlign: 'center', background: '#e6f7ff' }}>
                  <Statistic
                    title="覆盖国家"
                    value={geoDistribution.summary.countries}
                    suffix="个"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small" style={{ textAlign: 'center', background: '#fff2e8' }}>
                  <Statistic
                    title="已知位置"
                    value={geoDistribution.summary.knownLocations}
                    suffix="个"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small" style={{ textAlign: 'center', background: '#fff1f0' }}>
                  <Statistic
                    title="未知位置"
                    value={geoDistribution.summary.unknownLocations}
                    suffix="个"
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* 国家级别统计 TOP 10 */}
            {geoDistribution.countryStats && geoDistribution.countryStats.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <Title level={5}>国家/地区攻击统计 TOP 10</Title>
                <Row gutter={[16, 8]}>
                  {geoDistribution.countryStats.slice(0, 10).map((country, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card size="small" style={{ textAlign: 'center' }}>
                        <Text strong>{country.country}</Text>
                        <br />
                        <Text type="secondary">IP数: {country.ipCount}</Text>
                        <br />
                        <Text type="danger">攻击: {country.totalAttacks.toLocaleString()}</Text>
                        <br />
                        <Text type="primary">地区: {country.regionCount}</Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* 详细地区统计 TOP 15 */}
            {geoDistribution.geoDistribution && geoDistribution.geoDistribution.length > 0 && (
              <div>
                <Title level={5}>详细地区攻击统计 TOP 15</Title>
                <Row gutter={[16, 8]}>
                  {geoDistribution.geoDistribution.slice(0, 15).map((location, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card 
                        size="small" 
                        style={{ 
                          textAlign: 'center',
                          borderColor: location.attackPercentage > 10 ? '#ff4d4f' : 
                                     location.attackPercentage > 5 ? '#fa8c16' : '#d9d9d9'
                        }}
                      >
                        <Text strong style={{ fontSize: '12px' }}>{location.location}</Text>
                        <br />
                        <Text type="secondary">IP: {location.ipCount}</Text>
                        <br />
                        <Text type="danger">攻击: {location.totalAttacks.toLocaleString()}</Text>
                        <br />
                        <Text type="primary">占比: {location.attackPercentage}%</Text>
                        <br />
                        <Text type="warning">均值: {location.avgAttacksPerIP}</Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </Card>
        )}

        {/* 原有地理位置统计（保持兼容） */}
        {data?.statistics?.locationDistribution && data.statistics.locationDistribution.length > 0 && (
          <Card title="攻击来源地理分布 TOP 10（原始数据）" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 8]}>
              {data.statistics.locationDistribution.map((location, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <Text strong>{location._id || '未知'}</Text>
                    <br />
                    <Text type="secondary">IP数: {location.count}</Text>
                    <br />
                    <Text type="danger">攻击: {location.totalAttacks}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* 搜索和操作区域 */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                placeholder="搜索IP地址或归属地"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                style={{ width: '100%' }}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Text strong>排序：</Text>
                <Select
                  value={sortBy}
                  style={{ width: 120 }}
                  onChange={(value) => handleSortChange(value, sortOrder)}
                >
                  <Option value="attackCount">攻击次数</Option>
                  <Option value="ip">IP地址</Option>
                  <Option value="createdAt">创建时间</Option>
                  <Option value="lastSeenTime">最后发现</Option>
                  <Option value="updatedAt">更新时间</Option>
                </Select>
                <Select
                  value={sortOrder}
                  style={{ width: 80 }}
                  onChange={(value) => handleSortChange(sortBy, value)}
                >
                  <Option value="desc">降序</Option>
                  <Option value="asc">升序</Option>
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
              >
                刷新数据
              </Button>
            </Col>
          </Row>
        </Card>

        {/* 数据表格 */}
        <Card title="攻击IP数据">
          <Table
            columns={columns}
            dataSource={data?.attackIPs || []}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            size="middle"
          />
        </Card>

        {/* 使用说明 */}
        <Card title="使用说明" style={{ marginTop: '24px' }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="功能说明">
              攻击IP监控系统会自动收集和分析攻击IP数据，帮助管理员识别和防范恶意攻击。
            </Descriptions.Item>
            <Descriptions.Item label="服务控制">
              可以启动/停止监控服务，手动触发数据收集任务，或清空历史数据。
            </Descriptions.Item>
            <Descriptions.Item label="数据筛选">
              支持按IP地址、归属地搜索，可按攻击次数、时间等字段排序。
            </Descriptions.Item>
            <Descriptions.Item label="统计分析">
              提供攻击IP总数、攻击次数统计，以及地理位置分布分析。
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>
    </div>
  );
};

export default AdminAttackIPMonitor; 